// Channel logo — derives a stable color + initials from the channel name.
// When `logoUrl` is provided we try to render it as an <img>; on any load
// error we transparently swap to the gradient + initials chip (same fallback
// path used when no logoUrl is supplied at all). Mirrors the mobile pattern
// where ChannelCard always renders the first-letter chip on the accent color.

import { useState } from 'react';

interface ChannelLogoProps {
  readonly name: string;
  readonly logoUrl?: string | null;
  readonly size?: number;
}

const PALETTE = [
  ['#FF5C28', '#E84D1C'],
  ['#4a9eff', '#1c5ec0'],
  ['#7ac060', '#3b6d11'],
  ['#a090e8', '#5d4dd0'],
  ['#f0a030', '#854f0b'],
  ['#e05555', '#a32d2d'],
  ['#37b9c8', '#0c6c7a'],
  ['#d97aae', '#9b3a78'],
] as const;

function hash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function isHttpUrl(s: string | null | undefined): boolean {
  if (!s) return false;
  return /^https?:\/\//i.test(s.trim());
}

function GradientFallback({ name, size }: { readonly name: string; readonly size: number }) {
  const palette = PALETTE[hash(name) % PALETTE.length]!;
  const fontSize = Math.max(10, Math.round(size * 0.42));
  return (
    <span
      className="nw-channel-logo"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`,
        fontSize,
      }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

export function ChannelLogo({ name, logoUrl, size = 32 }: ChannelLogoProps) {
  // `errored` flips on the first load failure and pins the fallback for
  // the lifetime of this component instance — we don't want to retry the
  // broken URL on every re-render.
  const [errored, setErrored] = useState(false);

  if (!isHttpUrl(logoUrl) || errored) {
    return <GradientFallback name={name} size={size} />;
  }

  return (
    <img
      src={logoUrl!}
      alt={name}
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: 9,
        objectFit: 'cover',
        flexShrink: 0,
        background: 'var(--pill-bg)',
      }}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
    />
  );
}
